import React, { useEffect, useState } from 'react';
import {
  MyAccountFormData,
  MyAccountUserInfoFormData,
  PinChangeOption,
} from '../types';
import Swal from 'sweetalert2';
import {
  changePin,
  forgotPin,
  forgotPinChange,
} from '../../../../auth/AuthState';
import { FormChangeEvent, useClinicianProfile, useForm } from '../../../hooks';
import { isValidAustralianLandlineNumber } from '../utils/numberUtils';
import { isEmpty } from 'lodash';
import {
  ClinicianProfileFieldKey,
  ClinicianProfileFieldType,
} from '../../../../lib';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../store';

interface MyAccountContextProps {
  option: PinChangeOption;
  formData: MyAccountFormData;
  userInfoFormData: MyAccountUserInfoFormData;
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  userInfoOnInputChange: (
    event: FormChangeEvent,
    type: ClinicianProfileFieldType,
  ) => void;
  onInputChange: (event: FormChangeEvent) => void;
  handleCurrentPinSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  handleForgotPinCode: (event: React.MouseEvent<HTMLButtonElement>) => void;
  handleForgotPinSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  setOption: (option: PinChangeOption) => void;
  onValidateUserInfoInput: () => boolean;
  userInfoErrors: Partial<Record<ClinicianProfileFieldKey, string | undefined>>;
}
const MyAccountContext = React.createContext<MyAccountContextProps>({
  option: 'currentPin',
  isLoading: false,
  onValidateUserInfoInput: () => false,
  setIsLoading: () => {},
  formData: {
    currentPin: '',
    newPin: '',
    confirmPin: '',
    verificationCode: '',
  },
  userInfoErrors: {} as Record<ClinicianProfileFieldKey, string | undefined>,
  userInfoFormData: {
    phoneNumber: '',
    pagerNumber: '',
  },
  onInputChange: () => {},
  userInfoOnInputChange: () => {},
  handleCurrentPinSubmit: () => {},
  handleForgotPinCode: () => {},
  handleForgotPinSubmit: () => {},
  setOption: () => {},
});

export const useMyAccount = () => React.useContext(MyAccountContext);

const MyAccountProvider = ({ children }: { children: React.ReactNode }) => {
  const [initialzed, setInitialzed] = useState<boolean>(false);
  const [option, setOption] = useState<PinChangeOption>('currentPin');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { clinicianProfileFields } = useClinicianProfile();
  const [userInfoErrors, setUserInfoErrors] = useState<
    Partial<Record<ClinicianProfileFieldKey, string | undefined>>
  >({});

  const { details } = useSelector((state: RootState) => state.clinicianProfile);
  const { formState, onInputChange } = useForm<MyAccountFormData>({
    currentPin: '',
    newPin: '',
    confirmPin: '',
    verificationCode: '',
  });

  const onValidateUserInfoInput = () => {
    setUserInfoErrors({});

    const errors: Partial<
      Record<ClinicianProfileFieldKey, string | undefined>
    > = {};

    clinicianProfileFields.forEach((field) => {
      const fieldValue = userInfoFormState[field.key];

      if (field.key === 'medicineProfilePhoneNumber') {
        if (fieldValue && !isValidAustralianLandlineNumber(fieldValue)) {
          errors.medicineProfilePhoneNumber = 'Invalid landline phone number';
        }
      }
      // TODO：to have more cases for error handling
    });

    setUserInfoErrors(errors);
    return isEmpty(errors);
  };

  const { formState: userInfoFormState, onInputChange: userInfoOnInputChange } =
    useForm<MyAccountUserInfoFormData>({});

  const handleCurrentPinSubmit = (
    event: React.FormEvent<HTMLFormElement>,
  ): void => {
    event.preventDefault();
    if (formState.newPin === formState.confirmPin) {
      changePin(formState.currentPin, formState.newPin)
        .then((res) => {
          // Handle success
        })
        .catch((err) => console.log(err));
    } else {
      Swal.fire({
        icon: 'warning',
        text: "New PIN and confirmed new PIN don't match",
        showConfirmButton: true,
      });
    }
  };

  const handleForgotPinCode = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    event.preventDefault();
    forgotPin();
  };

  const handleForgotPinSubmit = (
    event: React.FormEvent<HTMLFormElement>,
  ): void => {
    event.preventDefault();
    if (formState.newPin === formState.confirmPin) {
      forgotPinChange(formState.verificationCode || '', formState.newPin)
        .then((res) => {
          // Handle success
        })
        .catch((err) => console.log(err));
    } else {
      Swal.fire({
        icon: 'warning',
        text: "New PIN and confirmed new PIN don't match",
        showConfirmButton: true,
      });
    }
  };

  const userInfoOnInputGuardChange = (
    event: FormChangeEvent,
    type: ClinicianProfileFieldType,
  ) => {
    let rule = true;
    switch (type) {
      case 'number':
        rule = /^\d+$/.test(event.target.value) || event.target.value === '';
        break;
      // TODO: to add more rule for different type if needed
    }
    if (rule) {
      userInfoOnInputChange(event);
    }
  };

  useEffect(() => {
    if (!initialzed) {
      if (!isEmpty(details)) {
        setInitialzed(true);
        clinicianProfileFields.forEach((field) => {
          userInfoOnInputChange({
            target: {
              name: field.key,
              value: details[field.key] || '',
            },
          });
        });
      }
    }
  }, [initialzed, details]);

  return (
    <MyAccountContext.Provider
      value={{
        isLoading,
        setIsLoading,
        option,
        setOption,
        userInfoErrors,
        formData: formState,
        onInputChange,
        handleCurrentPinSubmit,
        handleForgotPinCode,
        handleForgotPinSubmit,
        userInfoFormData: userInfoFormState,
        onValidateUserInfoInput,
        userInfoOnInputChange: userInfoOnInputGuardChange,
      }}
    >
      {children}
    </MyAccountContext.Provider>
  );
};

export default MyAccountProvider;
